import React, { useEffect, PropsWithChildren } from 'react';
import { useAuthStore } from '../../stores/authStore';

/**
 * AuthProvider component that handles app-level authentication validation
 * This should be placed at the root of your app to ensure authentication
 * state is properly validated on startup and throughout the app lifecycle
 */
const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const authStore = useAuthStore();

  useEffect(() => {
    let isMounted = true;
    
    // Validate authentication on app startup
    const validateAuthOnStartup = async () => {
      if (!isMounted) return;
      console.log('[AuthProvider] Starting authentication validation...');
      
      // If user appears to be authenticated
      if (authStore.isAuthenticated && authStore.token) {
        // Check if token is expired
        if (authStore.isTokenExpired()) {
          console.log('[AuthProvider] Token expired on startup');
          
          // For guest users, just logout
          if (authStore.isGuest) {
            console.log('[AuthProvider] Guest token expired, clearing auth');
            await authStore.logout();
            return;
          }
          
          // For regular users, try to refresh
          if (authStore.refreshToken) {
            console.log('[AuthProvider] Attempting token refresh on startup...');
            try {
              const isValid = await authStore.validateAndRefreshToken();
              if (isValid) {
                console.log('[AuthProvider] Token refresh successful');
              } else {
                console.log('[AuthProvider] Token refresh failed, user will be logged out');
              }
            } catch (error) {
              console.error('[AuthProvider] Token refresh error on startup:', error);
              await authStore.logout();
            }
          } else {
            console.log('[AuthProvider] No refresh token available, clearing auth');
            await authStore.logout();
          }
        } else {
          console.log('[AuthProvider] Token is valid, authentication maintained');
        }
      } else {
        console.log('[AuthProvider] No authentication found on startup');
      }
    };

    validateAuthOnStartup();

    // Reset API states on first load
    const resetApiStatesOnStartup = async () => {
      if (!isMounted) return;
      
      try {
        const { api } = await import('../../services/api');
        await api.resetAllStates();
        console.log('[AuthProvider] API states reset on startup');
      } catch (error) {
        console.warn('[AuthProvider] Failed to reset API states on startup:', error);
      }
    };
    
    resetApiStatesOnStartup();

    // TEMPORARILY DISABLED - Set up periodic token validation 
    // Re-enable this after fixing the 429 issues
    // const validationInterval = setInterval(() => {
    //   if (authStore.isAuthenticated) {
    //     console.log('[AuthProvider] Periodic token validation...');
    //     authStore.validateAndRefreshToken().catch(error => {
    //       console.error('[AuthProvider] Periodic validation failed:', error);
    //     });
    //   }
    // }, 10 * 60 * 1000); // 10 minutes

    // Dummy interval to satisfy the cleanup
    const validationInterval = setInterval(() => {}, 24 * 60 * 60 * 1000); // 24 hours (essentially disabled)

    // Cleanup interval on unmount
    return () => {
      isMounted = false;
      clearInterval(validationInterval);
    };
  }, [authStore]);

  // Listen for storage changes in other tabs
  useEffect(() => {
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key === 'dasoonegg-auth' && e.newValue === null) {
        // Auth was cleared in another tab
        console.log('[AuthProvider] Auth cleared in another tab, syncing logout');
        await authStore.logout();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [authStore]);

  return <>{children}</>;
};

export default AuthProvider;